import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import CustomerShippingTab from "@/components/CustomerShippingTab";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import ArtworkLibrary from "@/components/ArtworkLibrary";
import EditOrderSheet from "@/components/EditOrderSheet";
import PaymentDialog from "@/components/PaymentDialog";
import { TakePaymentDialog } from "@/components/TakePaymentDialog";
import ProductionPlanDialog from "@/components/ProductionPlanDialog";
import DuplicateOrderDialog from "@/components/DuplicateOrderDialog";
import { useAuth } from "@/context/AuthContext";
import { canOrder } from "@/lib/orderPerms";
import {
  ArrowLeft, Building2, Phone, Mail, MapPin, FileText, Star, Pencil,
  Printer, Send, MessageSquare, Package, Receipt, DollarSign, Clock,
  TrendingUp, Truck, Sparkles, Loader2, Plus, Copy, ExternalLink, Trash2,
  ChevronRight, User, ShoppingCart, Award, AlertTriangle, Info, Users,
  Image as ImageIcon, Zap, ClipboardCheck, Wallet, Factory, Download,
  FileDown, Upload, File as FileIcon,
  LayoutDashboard, Folder, StickyNote, Bookmark, Coins,
} from "lucide-react";
import { toast } from "sonner";

// Sekme kartları — soft pastel palet (aktif: güçlü border + hafif gölge)
const CUSTOMER_TABS = [
  { v: "overview", label: "Genel Bakış", Icon: LayoutDashboard,
    tone: "bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100/70 hover:border-blue-300 data-[state=active]:bg-blue-100 data-[state=active]:border-blue-500 data-[state=active]:text-blue-800 data-[state=active]:shadow-sm" },
  { v: "visits", label: "Ziyaret Geçmişi", Icon: Clock,
    tone: "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100/70 hover:border-emerald-300 data-[state=active]:bg-emerald-100 data-[state=active]:border-emerald-500 data-[state=active]:text-emerald-800 data-[state=active]:shadow-sm" },
  { v: "quotes", label: "Teklifler", Icon: FileText,
    tone: "bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100/70 hover:border-violet-300 data-[state=active]:bg-violet-100 data-[state=active]:border-violet-500 data-[state=active]:text-violet-800 data-[state=active]:shadow-sm" },
  { v: "orders", label: "Siparişler", Icon: ShoppingCart,
    tone: "bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100/70 hover:border-orange-300 data-[state=active]:bg-orange-100 data-[state=active]:border-orange-500 data-[state=active]:text-orange-800 data-[state=active]:shadow-sm" },
  { v: "products", label: "Ürün Bilgileri", Icon: Package,
    tone: "bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100/70 hover:border-teal-300 data-[state=active]:bg-teal-100 data-[state=active]:border-teal-500 data-[state=active]:text-teal-800 data-[state=active]:shadow-sm" },
  { v: "finance", label: "Finans", Icon: Coins,
    tone: "bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100/70 hover:border-sky-300 data-[state=active]:bg-sky-100 data-[state=active]:border-sky-500 data-[state=active]:text-sky-800 data-[state=active]:shadow-sm" },
  { v: "shipping", label: "Kargo", Icon: Truck,
    tone: "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100/70 hover:border-rose-300 data-[state=active]:bg-rose-100 data-[state=active]:border-rose-500 data-[state=active]:text-rose-800 data-[state=active]:shadow-sm" },
  { v: "docs", label: "Evraklar", Icon: Folder,
    tone: "bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100/70 hover:border-purple-300 data-[state=active]:bg-purple-100 data-[state=active]:border-purple-500 data-[state=active]:text-purple-800 data-[state=active]:shadow-sm" },
  { v: "notes", label: "Notlar", Icon: StickyNote,
    tone: "bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100/70 hover:border-amber-300 data-[state=active]:bg-amber-100 data-[state=active]:border-amber-500 data-[state=active]:text-amber-800 data-[state=active]:shadow-sm" },
  { v: "options", label: "Opsiyonlar", Icon: Bookmark,
    tone: "bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100/70 hover:border-cyan-300 data-[state=active]:bg-cyan-100 data-[state=active]:border-cyan-500 data-[state=active]:text-cyan-800 data-[state=active]:shadow-sm" },
  { v: "ai", label: "AI Analiz", Icon: Sparkles,
    tone: "bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200/70 hover:border-slate-400 data-[state=active]:bg-slate-200 data-[state=active]:border-slate-500 data-[state=active]:text-slate-800 data-[state=active]:shadow-sm" },
];

const RATING_STYLES = {
  "A+": "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40",
  "A":  "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  "B":  "bg-amber-500/15  text-amber-600 dark:text-amber-400 border-amber-500/40",
  "C":  "bg-rose-500/15   text-rose-600 dark:text-rose-400 border-rose-500/40",
};

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("tr-TR"); } catch { return "—"; } };
const daysBetween = (iso) => { if (!iso) return null; return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); };

export default function CustomerProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [takePayOpen, setTakePayOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const [editOrder, setEditOrder] = useState(null);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [productionOrder, setProductionOrder] = useState(null);
  const [dupOrder, setDupOrder] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/customers/${id}/profile`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);  // eslint-disable-line

  const handleOrderAction = (action, order) => {
    if (action === "edit") setEditOrder(order);
    else if (action === "payment") setPaymentOrder(order);
    else if (action === "production") setProductionOrder(order);
    else if (action === "duplicate") setDupOrder(order);
  };

  const c = data?.customer || {};
  const orders = data?.orders || [];
  const purchases = data?.purchases || [];
  const finance = data?.finance || {};

  // KPIs — FT fatura belgeleri (order_source_id) hariç: aynı ticari satış çift sayılmaz
  const kpis = useMemo(() => {
    const sales = orders.filter((o) => !o.order_source_id);
    const activeOrders = sales.filter((o) => o.stage !== "tamamlandi" && o.stage !== "iptal" && o.stage !== "teklif");
    const quotes = sales.filter((o) => o.stage === "teklif");
    const completed = sales.filter((o) => o.stage === "tamamlandi");
    const pendingProd = sales.filter((o) => !["tamamlandi","iptal","teklif","siparis_onay"].includes(o.stage));
    const totalRevenue = completed.reduce((s, o) => s + (o.total_price || 0), 0);
    const avgOrder = sales.length ? sales.reduce((s, o) => s + (o.total_price || 0), 0) / sales.length : 0;
    const lastOrder = sales.length ? sales[0] : null;
    return {
      totalRevenue,
      totalOrders: orders.length,
      pendingOrders: activeOrders.length,
      productionOrders: pendingProd.length,
      quotesCount: quotes.length,
      lastOrderDate: lastOrder?.created_at,
      lastOrderDays: daysBetween(lastOrder?.created_at),
      avgOrder,
      completedCount: completed.length,
      // Finance from backend (Phase B) — ERP outstanding=0 ise Bizim Hesap
      // bakiyesine (bh_balance) fallback yap (BH'den aktarılmış müşteriler).
      outstanding: (Math.abs(Number(c.bh_balance ?? 0)) > 0 && !(finance.outstanding > 0))
        ? Number(c.bh_balance)
        : (finance.outstanding || 0),
      overdueAmount: finance.overdue_amount || 0,
      upcomingAmount: finance.upcoming_amount || 0,
      oldestOverdueDays: finance.oldest_overdue_days || 0,
      openInvoicesCount: finance.open_invoices_count || 0,
      financeStatus: finance.status || "yesil",
      riskExceeded: !!finance.risk_exceeded,
    };
  }, [orders, finance, c.bh_balance]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 text-primary animate-spin" />
      </div>
    );
  }

  const whatsappHref = c.phone ? `https://wa.me/${(c.phone).replace(/[^0-9]/g, "")}` : null;
  const mailHref = c.email ? `mailto:${c.email}` : null;

  return (
    <div className="bg-background min-h-screen text-foreground">
      {/* ---- Top toolbar ---- */}
      <div className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-3 flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate("/musteri")}
            className="p-1.5 rounded-sm hover:bg-muted text-muted-foreground hover:text-foreground"
            data-testid="back-to-customers"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 flex items-center gap-2 min-w-0">
            <Users className="w-5 h-5 text-primary shrink-0" />
            <h1 className="text-lg lg:text-xl font-bold truncate" data-testid="customer-name">
              {c.name}
            </h1>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
              Aktif Müşteri
            </span>
            {c.is_vip && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/40">
                <Star className="w-3 h-3" /> VIP
              </span>
            )}
            {c.rating && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold border ${RATING_STYLES[c.rating] || RATING_STYLES.B}`}>
                {c.rating}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <Button data-testid="quick-new-quote" size="sm" onClick={() => navigate(`/siparis-sihirbazi?mode=quote&customer=${id}`)} className="rounded-sm">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> Teklif
            </Button>
            <Button data-testid="quick-new-order" size="sm" onClick={() => navigate(`/siparis-sihirbazi?customer=${id}`)} className="rounded-sm">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Sipariş
            </Button>
            <Button data-testid="quick-take-payment" size="sm" onClick={() => setTakePayOpen(true)}
                    className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white">
              <Receipt className="w-3.5 h-3.5 mr-1.5" /> Ödeme Al
            </Button>
            <Button data-testid="edit-customer" size="sm" variant="outline" onClick={() => setEditOpen(true)} className="rounded-sm">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Düzenle
            </Button>
            {mailHref && (
              <a href={mailHref} data-testid="mail-btn" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-border hover:bg-muted text-sm">
                <Mail className="w-3.5 h-3.5" />
              </a>
            )}
            {whatsappHref && (
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-btn"
                 className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-sm hover:bg-emerald-500/20">
                <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
            <button data-testid="print-btn" onClick={() => window.print()} className="p-2 rounded-sm border border-border hover:bg-muted">
              <Printer className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* ---- KPI Cards ---- */}
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 pt-5" data-testid="kpi-grid">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard testId="kpi-total-revenue" icon={DollarSign} color="emerald"
            label="Toplam Alışveriş" value={`₺${fmt(kpis.totalRevenue)}`}
            sub={`${kpis.completedCount} tamamlanan`}
            onClick={() => setTab("orders")} />
          <KpiCard testId="kpi-total-orders" icon={ShoppingCart} color="emerald"
            label="Toplam Sipariş" value={kpis.totalOrders} sub="tüm zamanlar"
            onClick={() => setTab("orders")} />
          <KpiCard testId="kpi-avg-order" icon={TrendingUp} color="blue"
            label="Ort. Sipariş" value={`₺${fmt(kpis.avgOrder)}`} sub="tutar"
            onClick={() => setTab("orders")} />
          <KpiCard testId="kpi-last-order" icon={Sparkles} color="blue"
            label="Son Sipariş" value={kpis.lastOrderDays === null ? "—" : `${kpis.lastOrderDays}g`}
            sub={fmtDate(kpis.lastOrderDate)}
            onClick={() => setTab("orders")} />
          <KpiCard testId="kpi-pending-quotes" icon={Clock} color="orange"
            label="Bekleyen Teklif" value={kpis.quotesCount}
            sub={`${kpis.pendingOrders} açık sipariş`}
            onClick={() => setTab("quotes")} />
          <KpiCard testId="kpi-in-production" icon={Package} color="orange"
            label="Üretimdeki İşler" value={kpis.productionOrders} sub="aktif iş emri"
            onClick={() => setTab("orders")} />
          <KpiCard testId="kpi-cari-balance" icon={Receipt}
            color={kpis.overdueAmount > 0 ? "red" : "emerald"}
            label="Cari Bakiye" value={`₺${fmt(kpis.outstanding)}`}
            sub={`${kpis.openInvoicesCount} açık fatura`}
            onClick={() => setTab("finance")} />
          <KpiCard testId="kpi-overdue" icon={AlertTriangle} color="red"
            label="Vadesi Geçmiş" value={`₺${fmt(kpis.overdueAmount)}`}
            sub={kpis.oldestOverdueDays > 0 ? `en eski ${kpis.oldestOverdueDays}g` : "yok"}
            onClick={() => setTab("finance")} />
          <KpiCard testId="kpi-rating" icon={Award} color="purple"
            label="Müşteri Puanı" value={c.rating || "B"}
            sub={c.is_vip ? "VIP Müşteri" : "Standart"} />
          <KpiCard testId="kpi-sales-rep" icon={User} color="gray"
            label="Müşteri Temsilcisi"
            value={c.sales_rep_name || "Atanmadı"}
            sub={c.sales_rep_email || (c.sales_rep_name ? "temsilci" : "düzenle → ata")}
            onClick={() => setEditOpen(true)} />
        </div>
      </div>

      {/* ---- Tabs ---- */}
      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-transparent border-0 p-0 flex flex-wrap gap-2 h-auto">
            {CUSTOMER_TABS.map(({ v, label, Icon, tone }) => (
              <TabsTrigger
                key={v}
                data-testid={`tab-${v}`}
                value={v}
                className={`rounded-md border-2 px-3.5 py-2 text-[11px] font-semibold uppercase tracking-wider flex items-center gap-1.5 cursor-pointer transition-colors ${tone}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Genel Bakış */}
          <TabsContent value="overview" className="mt-5 space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* Company info */}
              <Card className="rounded-sm bg-card border-border p-5 lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest">Müşteri Bilgileri</h3>
                  <button onClick={() => setEditOpen(true)} className="text-xs text-primary hover:underline">
                    Düzenle
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoRow icon={Building2} label="Firma Adı" value={c.name} />
                  <InfoRow icon={FileText} label="Vergi No" value={c.tax_no || "—"} mono />
                  <InfoRow icon={Phone} label="Telefon" value={c.phone || "—"} mono />
                  <InfoRow icon={Mail} label="E-posta" value={c.email || "—"} />
                  <InfoRow icon={MapPin} label="Adres" value={c.address || "—"} full />
                  <InfoRow icon={FileText} label="Notlar" value={c.notes || "—"} full />
                </div>
              </Card>

              {/* Map */}
              <Card className="rounded-sm bg-card border-border overflow-hidden p-1" data-testid="customer-map-card">
                {c.address ? (
                  <iframe
                    title="Konum"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(c.address)}&output=embed`}
                    className="w-full h-full min-h-[220px] border-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full min-h-[220px] flex flex-col items-center justify-center text-muted-foreground p-8 text-center">
                    <MapPin className="w-8 h-8 mb-2 opacity-40" />
                    <div className="text-sm">Adres girilmemiş</div>
                  </div>
                )}
              </Card>
            </div>

            {/* Recent activity: orders + quotes side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Card className="rounded-sm bg-card border-border p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest">Son Siparişler</h3>
                  <button onClick={() => setTab("orders")} className="text-xs text-primary hover:underline">Tümünü Gör →</button>
                </div>
                <MiniOrdersTable orders={orders.filter((o) => o.stage !== "teklif").slice(0, 5)} />
              </Card>
              <Card className="rounded-sm bg-card border-border p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold uppercase tracking-widest">Son Teklifler</h3>
                  <button onClick={() => setTab("quotes")} className="text-xs text-primary hover:underline">Tümünü Gör →</button>
                </div>
                <MiniOrdersTable orders={orders.filter((o) => o.stage === "teklif").slice(0, 5)} />
              </Card>
            </div>

            {/* AI cards row */}
            <AiAnalysisRow kpis={kpis} customer={c} />

            {/* Activity Timeline */}
            <TimelinePanel customerId={id} />

            {/* Related tasks */}
            <CustomerTasks customerId={id} />
          </TabsContent>

          {/* Ziyaret Geçmişi */}
          <TabsContent value="visits" className="mt-5">
            <VisitsTab customerId={id} />
          </TabsContent>

          {/* Teklifler / Siparişler — full tables */}
          <TabsContent value="quotes" className="mt-5">
            <OrdersTable orders={orders.filter((o) => o.stage === "teklif")} onAction={handleOrderAction} />
          </TabsContent>
          <TabsContent value="orders" className="mt-5">
            <OrdersTable orders={orders.filter((o) => o.stage !== "teklif")} onAction={handleOrderAction} />
          </TabsContent>

          {/* Ürün Bilgileri — reuse ArtworkLibrary */}
          <TabsContent value="products" className="mt-5">
            <ArtworkLibrary customerId={id} />
          </TabsContent>

          <TabsContent value="finance" className="mt-5">
            <FinanceTab kpis={kpis} orders={orders} finance={finance} />
          </TabsContent>

          <TabsContent value="shipping" className="mt-5">
            <CustomerShippingTab customerId={id} />
          </TabsContent>

          <TabsContent value="docs" className="mt-5">
            <DocumentsTab customerId={id} />
          </TabsContent>

          <TabsContent value="notes" className="mt-5">
            <NotesTab customerId={id} />
          </TabsContent>

          <TabsContent value="options" className="mt-5">
            <CustomerStockOptions customerId={id} />
          </TabsContent>
          <TabsContent value="ai" className="mt-5">
            <AiDeepAnalysis customerId={id} customerName={c.name} kpis={kpis} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit dialog */}
      <EditCustomerDialog open={editOpen} onOpenChange={setEditOpen} customer={c} onSaved={load} />
      <TakePaymentDialog customer={c} open={takePayOpen} onOpenChange={setTakePayOpen} onSaved={load} />
      <EditOrderSheet
        order={editOrder}
        open={!!editOrder}
        onOpenChange={(v) => !v && setEditOrder(null)}
        onSaved={load}
      />
      <PaymentDialog
        order={paymentOrder}
        open={!!paymentOrder}
        onOpenChange={(v) => !v && setPaymentOrder(null)}
        onSaved={load}
      />
      <ProductionPlanDialog
        order={productionOrder}
        open={!!productionOrder}
        onOpenChange={(v) => !v && setProductionOrder(null)}
        onDone={load}
      />
      <DuplicateOrderDialog
        order={dupOrder}
        open={!!dupOrder}
        currentCustomerId={id}
        onOpenChange={(v) => !v && setDupOrder(null)}
        onDuplicated={load}
      />
    </div>
  );
}

// ---- KPI Card ---------------------------------------------------------------
function KpiCard({ icon: Icon, label, value, sub, color = "blue", onClick, testId }) {
  const bg = {
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
    blue:    "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
    orange:  "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
    red:     "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30",
    purple:  "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30",
    gray:    "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/30",
    // Legacy aliases retained for callers elsewhere
    amber:   "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
    pink:    "bg-pink-500/10 text-pink-600 dark:text-pink-400 border-pink-500/30",
    cyan:    "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/30",
  }[color] || "bg-blue-500/10 text-blue-600 border-blue-500/30";

  const clickable = typeof onClick === "function";
  const Wrapper = clickable ? "button" : "div";
  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      data-testid={testId}
      className={`text-left rounded-sm bg-card border border-border p-3 transition-all ${
        clickable ? "hover:shadow-lg hover:border-primary/40 hover:-translate-y-0.5 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40" : ""
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className={`w-8 h-8 rounded-sm flex items-center justify-center border ${bg}`}>
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </div>
        {clickable && (
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
        )}
      </div>
      <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground truncate">{label}</div>
      <div className="text-lg font-bold text-foreground mt-0.5 font-mono truncate">{value}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>
    </Wrapper>
  );
}

function InfoRow({ icon: Icon, label, value, mono, full }) {
  return (
    <div className={`flex items-start gap-3 ${full ? "md:col-span-2" : ""}`}>
      <div className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
        <div className={`text-sm text-foreground ${mono ? "font-mono" : ""} truncate`}>{value || "—"}</div>
      </div>
    </div>
  );
}

function MiniOrdersTable({ orders }) {
  if (orders.length === 0) {
    return <div className="text-xs text-muted-foreground italic text-center py-6">Kayıt yok</div>;
  }
  return (
    <div className="space-y-2">
      {orders.map((o) => (
        <div key={o.id} className="flex items-center justify-between gap-2 p-2 rounded-sm hover:bg-muted transition-colors">
          <div className="min-w-0">
            <div className="text-xs font-mono text-primary font-bold">{o.order_no}</div>
            <div className="text-[10px] text-muted-foreground truncate">{o.product_name}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-sm font-bold font-mono">₺{fmt(o.total_price)}</div>
            <div className="text-[10px] text-muted-foreground">{fmtDate(o.created_at)}</div>
          </div>
          <Badge variant="outline" className="text-[9px] font-mono uppercase shrink-0">{o.stage}</Badge>
        </div>
      ))}
    </div>
  );
}

function OrdersTable({ orders, onNavigate, onAction }) {
  const { can } = useAuth();
  if (orders.length === 0) return <PlaceholderCard title="Kayıt yok" note="" />;
  return (
    <Card className="rounded-sm bg-card border-border overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] uppercase tracking-widest font-mono">No</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-mono">Tarih</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-mono">Ürün</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest font-mono">Adet</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest font-mono">Tutar ₺</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-mono">Termin</TableHead>
              <TableHead className="text-[10px] uppercase tracking-widest font-mono">Aşama</TableHead>
              <TableHead className="text-right text-[10px] uppercase tracking-widest font-mono">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} className="border-border hover:bg-muted">
                <TableCell className="font-mono text-xs text-primary font-semibold">{o.order_no}</TableCell>
                <TableCell className="text-xs font-mono">{fmtDate(o.created_at)}</TableCell>
                <TableCell className="text-sm">{o.product_name}</TableCell>
                <TableCell className="text-right font-mono">{o.quantity}</TableCell>
                <TableCell className="text-right font-mono font-bold">₺{fmt(o.total_price)}</TableCell>
                <TableCell className="text-xs font-mono">{fmtDate(o.due_date)}</TableCell>
                <TableCell><Badge variant="outline" className="text-[9px] font-mono uppercase">{o.stage}</Badge></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {canOrder(can, "edit", o) && <button data-testid={`cp-edit-${o.id}`} onClick={() => onAction?.("edit", o)}
                    title="Düzenle" className="h-7 w-7 inline-flex items-center justify-center text-muted-foreground hover:text-foreground rounded-sm">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>}
                  <a href={`/api/orders/${o.id}/pdf`} target="_blank" rel="noreferrer"
                    data-testid={`cp-pdf-${o.id}`} title="PDF İndir"
                    className="h-7 w-7 inline-flex items-center justify-center text-muted-foreground hover:text-foreground rounded-sm">
                    <FileText className="w-3.5 h-3.5" />
                  </a>
                  <button data-testid={`cp-payment-${o.id}`} onClick={() => onAction?.("payment", o)}
                    title="Ödeme Al" className="h-7 w-7 inline-flex items-center justify-center text-emerald-500 hover:bg-emerald-500/10 rounded-sm">
                    <Wallet className="w-3.5 h-3.5" />
                  </button>
                  <button data-testid={`cp-production-${o.id}`} onClick={() => onAction?.("production", o)}
                    title="Reçeteden Üretim" className="h-7 w-7 inline-flex items-center justify-center text-purple-500 hover:bg-purple-500/10 rounded-sm">
                    <Factory className="w-3.5 h-3.5" />
                  </button>
                  {canOrder(can, "add", o) && <button data-testid={`cp-duplicate-${o.id}`} onClick={() => onAction?.("duplicate", o)}
                    title="Kopyala" className="h-7 w-7 inline-flex items-center justify-center text-blue-500 hover:bg-blue-500/10 rounded-sm">
                    <Copy className="w-3.5 h-3.5" />
                  </button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function FinanceTab({ kpis, orders, finance = {} }) {
  const [payments, setPayments] = useState([]);
  const params = useParams();
  useEffect(() => {
    if (!params.id) return;
    api.get(`/finance/payments?customer_id=${params.id}`)
      .then(({ data }) => setPayments(data || []))
      .catch(() => {});
  }, [params.id, kpis.outstanding]);

  const statusPalette = {
    yesil: { label: "Sorun yok", cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/40" },
    sari: { label: "Yaklaşan vade", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/40" },
    turuncu: { label: "1-15 gün gecikmiş", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/40" },
    kirmizi: { label: "15+ gün gecikmiş", cls: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/40" },
    koyu_kirmizi: { label: "30+ gün gecikmiş — KRİTİK", cls: "bg-red-700/25 text-red-700 dark:text-red-300 border-red-700/60" },
  };
  const st = statusPalette[finance.status] || statusPalette.yesil;
  const overdueOrders = finance.overdue_orders || [];
  const upcomingOrders = finance.upcoming_orders || [];

  return (
    <div className="space-y-5" data-testid="finance-tab">
      {/* Statement PDF links */}
      <Card className="rounded-sm bg-card border-border p-4" data-testid="statement-links-card">
        <div className="flex items-center gap-2 mb-3">
          <FileDown className="w-4 h-4 text-blue-500" />
          <div className="text-sm font-bold uppercase tracking-wider">Ekstre & Mutabakat</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <a href={`/api/customers/${params.id}/statement.pdf?variant=extract`} target="_blank" rel="noreferrer"
            data-testid="statement-extract-link"
            className="rounded-sm border border-border bg-muted hover:border-blue-500 hover:bg-blue-500/5 px-3 py-2 text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            <div>
              <div className="font-semibold">Hesap Ekstresi</div>
              <div className="text-[10px] text-muted-foreground">Sipariş hareketleri + özet</div>
            </div>
          </a>
          <a href={`/api/customers/${params.id}/statement.pdf?variant=detailed`} target="_blank" rel="noreferrer"
            data-testid="statement-detailed-link"
            className="rounded-sm border border-border bg-muted hover:border-blue-500 hover:bg-blue-500/5 px-3 py-2 text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            <div>
              <div className="font-semibold">Detaylı Ekstre</div>
              <div className="text-[10px] text-muted-foreground">+ tahsilat detayı</div>
            </div>
          </a>
          <a href={`/api/customers/${params.id}/statement.pdf?variant=reconciliation`} target="_blank" rel="noreferrer"
            data-testid="statement-reconciliation-link"
            className="rounded-sm border border-border bg-muted hover:border-blue-500 hover:bg-blue-500/5 px-3 py-2 text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-500" />
            <div>
              <div className="font-semibold">Mutabakat Mektubu</div>
              <div className="text-[10px] text-muted-foreground">İmza alanlı PDF</div>
            </div>
          </a>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <FinanceStat label="Toplam Ciro" value={`₺${fmt(kpis.totalRevenue)}`} color="emerald" />
        <FinanceStat label="Açık Bakiye" value={`₺${fmt(finance.outstanding || 0)}`} color="orange" testId="finance-outstanding" />
        <FinanceStat label="Vadesi Geçmiş" value={`₺${fmt(finance.overdue_amount || 0)}`} color="red" testId="finance-overdue" />
        <FinanceStat label="Yaklaşan Vade (7g)" value={`₺${fmt(finance.upcoming_amount || 0)}`} color="amber" testId="finance-upcoming" />
      </div>

      <Card className={`rounded-sm border p-5 ${st.cls}`} data-testid="finance-status-banner">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5" />
          <div>
            <div className="text-[10px] uppercase tracking-widest font-mono opacity-80">Ödeme Durumu</div>
            <div className="text-sm font-bold">{st.label}</div>
          </div>
          {finance.risk_limit > 0 && (
            <div className="ml-auto text-right">
              <div className="text-[10px] uppercase tracking-widest font-mono opacity-80">Risk Limiti</div>
              <div className="text-sm font-mono font-bold">
                ₺{fmt(finance.outstanding || 0)} / ₺{fmt(finance.risk_limit)}
              </div>
              {finance.risk_exceeded && <div className="text-[10px] font-semibold mt-0.5">⚠ AŞILDI</div>}
            </div>
          )}
        </div>
      </Card>

      {overdueOrders.length > 0 && (
        <Card className="rounded-sm bg-card border-border overflow-hidden" data-testid="overdue-orders-card">
          <div className="px-4 py-3 border-b border-border bg-red-500/5 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-500" />
            <div className="text-sm font-bold uppercase tracking-wider text-red-500">Vadesi Geçmiş Ödemeler</div>
            <Badge className="ml-auto rounded-sm bg-red-500/15 text-red-500 border-red-500/40 text-[10px]">{overdueOrders.length}</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Sipariş</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Vade</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Gün</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Bakiye</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {overdueOrders.map((o) => (
                <TableRow key={o.id} className="border-border" data-testid={`overdue-row-${o.order_no}`}>
                  <TableCell className="font-mono text-xs text-primary font-semibold">{o.order_no}</TableCell>
                  <TableCell className="text-xs font-mono">{fmtDate(o.due_date)}</TableCell>
                  <TableCell className={`text-right text-xs font-mono font-bold ${o.days_overdue > 30 ? "text-red-700" : o.days_overdue > 15 ? "text-red-500" : "text-blue-500"}`}>
                    {o.days_overdue} gün
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold text-red-500">₺{fmt(o.remaining)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {upcomingOrders.length > 0 && (
        <Card className="rounded-sm bg-card border-border overflow-hidden" data-testid="upcoming-orders-card">
          <div className="px-4 py-3 border-b border-border bg-amber-500/5 flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <div className="text-sm font-bold uppercase tracking-wider text-amber-500">Yaklaşan Vadeler (7 Gün)</div>
            <Badge className="ml-auto rounded-sm bg-amber-500/15 text-amber-500 border-amber-500/40 text-[10px]">{upcomingOrders.length}</Badge>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Sipariş</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Vade</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Kalan</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Bakiye</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {upcomingOrders.map((o) => (
                <TableRow key={o.id} className="border-border">
                  <TableCell className="font-mono text-xs text-primary font-semibold">{o.order_no}</TableCell>
                  <TableCell className="text-xs font-mono">{fmtDate(o.due_date)}</TableCell>
                  <TableCell className="text-right text-xs font-mono text-amber-500 font-semibold">
                    {o.days_until_due === 0 ? "bugün" : `${o.days_until_due}g sonra`}
                  </TableCell>
                  <TableCell className="text-right font-mono font-bold">₺{fmt(o.remaining)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Payments history */}
      <Card className="rounded-sm bg-card border-border overflow-hidden" data-testid="payments-history-card">
        <div className="px-4 py-3 border-b border-border bg-emerald-500/5 flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-emerald-500" />
          <div className="text-sm font-bold uppercase tracking-wider text-emerald-500">Ödeme Geçmişi</div>
          <Badge className="ml-auto rounded-sm bg-emerald-500/15 text-emerald-500 border-emerald-500/40 text-[10px]">{payments.length}</Badge>
        </div>
        {payments.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Henüz tahsilat kaydı yok. Sipariş listesinden "Ödeme Al" ile ekleyebilirsiniz.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Tarih</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Sipariş</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Yöntem</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono">Makbuz</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-mono text-right">Tutar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.slice(0, 25).map((p) => (
                <TableRow key={p.id} className="border-border" data-testid={`payment-row-${p.id}`}>
                  <TableCell className="text-xs font-mono">{fmtDate(p.payment_date)}</TableCell>
                  <TableCell className="font-mono text-xs text-primary">{p.order_no}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] uppercase font-mono">{p.method}</Badge></TableCell>
                  <TableCell className="text-xs font-mono">{p.receipt_no || "—"}</TableCell>
                  <TableCell className="text-right font-mono font-bold text-emerald-500">₺{fmt(p.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
function FinanceStat({ label, value, color, testId }) {
  const cls = {
    emerald: "text-emerald-600 dark:text-emerald-400",
    orange:  "text-blue-600 dark:text-blue-400",
    blue:    "text-blue-600 dark:text-blue-400",
    purple:  "text-purple-600 dark:text-purple-400",
    red:     "text-red-600 dark:text-red-400",
    amber:   "text-amber-600 dark:text-amber-400",
  }[color];
  return (
    <Card className="rounded-sm bg-card border-border p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold font-mono ${cls} mt-1`}>{value}</div>
    </Card>
  );
}

function PlaceholderCard({ title, note }) {
  return (
    <Card className="rounded-sm bg-card border-border p-12 text-center">
      <div className="w-12 h-12 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
        <Info className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="text-lg font-bold mb-1">{title}</div>
      {note && <div className="text-sm text-muted-foreground">{note}</div>}
    </Card>
  );
}

// ---- AI static analysis row ------------------------------------------------
function AiAnalysisRow({ kpis, customer }) {
  const insights = useMemo(() => {
    const arr = [];
    if (kpis.lastOrderDays !== null && kpis.lastOrderDays > 42) {
      arr.push({ level: "warn", icon: AlertTriangle, title: "İlgi Azalıyor", body: `Bu müşteri ${kpis.lastOrderDays} gündür sipariş vermedi. Ziyaret önerilir.` });
    }
    if (kpis.totalOrders > 5 && kpis.avgOrder > 50000) {
      arr.push({ level: "good", icon: Award, title: "Değerli Müşteri", body: `${kpis.totalOrders} sipariş · ortalama ₺${fmt(kpis.avgOrder)}. VIP potansiyel.` });
    }
    if (kpis.quotesCount > 3 && kpis.totalOrders < kpis.quotesCount) {
      arr.push({ level: "warn", icon: TrendingUp, title: "Dönüşüm Düşük", body: `${kpis.quotesCount} teklif verildi, ${kpis.totalOrders} siparişe döndü. Takip önerilir.` });
    }
    if (kpis.productionOrders > 0) {
      arr.push({ level: "info", icon: Package, title: "Üretimde", body: `${kpis.productionOrders} sipariş şu an üretim hattında. Termin takibi yapın.` });
    }
    if (arr.length === 0) {
      arr.push({ level: "info", icon: Info, title: "Sağlıklı İlişki", body: "Bu müşteride şu an dikkat gerektiren bir sinyal yok." });
    }
    return arr.slice(0, 4);
  }, [kpis]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" /> Yapay Zeka Analizi
        </h3>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {insights.map((it, i) => {
          const Icon = it.icon;
          const cls = {
            warn: "border-amber-500/40 bg-amber-500/5",
            good: "border-emerald-500/40 bg-emerald-500/5",
            info: "border-blue-500/40 bg-blue-500/5",
          }[it.level];
          const iconCls = {
            warn: "text-amber-600 dark:text-amber-400",
            good: "text-emerald-600 dark:text-emerald-400",
            info: "text-blue-600 dark:text-blue-400",
          }[it.level];
          return (
            <Card key={i} data-testid={`ai-insight-${it.level}`} className={`rounded-sm border p-4 ${cls}`}>
              <Icon className={`w-5 h-5 ${iconCls} mb-2`} />
              <div className="text-sm font-bold mb-1">{it.title}</div>
              <div className="text-xs text-muted-foreground leading-relaxed">{it.body}</div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ---- AI Deep Analysis (Copilot-powered) ------------------------------------
function AiDeepAnalysis({ customerId, customerName, kpis }) {
  const [analysis, setAnalysis] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true); setAnalysis("");
    try {
      const question = `${customerName} müşterisi için derinlemesine analiz yap. Bu müşterinin sipariş geçmişini incele ve şu başlıklarda madde madde Türkçe rapor ver: 1) Satın alma eğilimi 2) Ortalama sipariş tutarı yorumu (₺${fmt(kpis.avgOrder)}) 3) Son 12 aydaki değişim tahmini 4) Tekrar sipariş tahmini 5) Çapraz satış önerisi 6) Ödeme davranışı riski. Kısa, net, satış ekibine yönelik ol.`;
      const { data } = await api.post("/copilot/chat", { message: question });
      setAnalysis(data.message?.content || "");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <AiAnalysisRow kpis={kpis} customer={{}} />
      <Card className="rounded-sm bg-card border-border p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Copilot Derin Analiz
            </h3>
            <div className="text-xs text-muted-foreground mt-0.5">
              Claude Sonnet 4.6 ile müşteri profili raporu üretin.
            </div>
          </div>
          <Button data-testid="ai-deep-run" onClick={run} disabled={loading} className="rounded-sm">
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            {analysis ? "Yeniden Analiz Et" : "Analiz Başlat"}
          </Button>
        </div>
        {analysis && (
          <div data-testid="ai-deep-result" className="text-sm whitespace-pre-wrap bg-muted/40 rounded-sm p-4 leading-relaxed">
            {analysis}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---- Edit dialog ------------------------------------------------------------
function EditCustomerDialog({ open, onOpenChange, customer, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState([]);
  useEffect(() => { if (open) setForm({ ...customer }); }, [open, customer]);
  // Müşteri Temsilcisi seçimi doğrudan Çalışanlar (employees) kayıtlarından
  useEffect(() => {
    if (!open) return;
    api.get("/employees").then((r) => setEmployees(r.data || [])).catch(() => {});
  }, [open]);

  const repValue = form.sales_rep_employee_id || (form.sales_rep_name ? "__custom" : "none");
  const onRepChange = (v) => {
    if (v === "none") {
      setForm({ ...form, sales_rep_employee_id: "", sales_rep_name: "", sales_rep_email: "" });
    } else if (v === "__custom") {
      // legacy serbest metin temsilci — değer korunur
    } else {
      const emp = employees.find((e) => e.id === v);
      if (emp) setForm({ ...form, sales_rep_employee_id: emp.id, sales_rep_name: emp.name, sales_rep_email: emp.email || "" });
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/customers/${customer.id}`, {
        name: form.name, phone: form.phone, email: form.email,
        address: form.address, tax_no: form.tax_no, notes: form.notes,
        is_vip: !!form.is_vip, rating: form.rating || "B",
        sales_rep_name: form.sales_rep_name || "",
        sales_rep_email: form.sales_rep_email || "",
        sales_rep_employee_id: form.sales_rep_employee_id || "",
        credit_limit: Number(form.credit_limit) || 0,
        risk_limit: Number(form.risk_limit) || 0,
      });
      toast.success("Kaydedildi");
      onOpenChange(false);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle>Müşteri Düzenle</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div><Label className="text-xs uppercase font-mono text-muted-foreground">Firma Adı</Label>
            <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase font-mono text-muted-foreground">Telefon</Label>
              <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label className="text-xs uppercase font-mono text-muted-foreground">Vergi No</Label>
              <Input value={form.tax_no || ""} onChange={(e) => setForm({ ...form, tax_no: e.target.value })} className="rounded-sm mt-1" /></div>
          </div>
          <div><Label className="text-xs uppercase font-mono text-muted-foreground">E-posta</Label>
            <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs uppercase font-mono text-muted-foreground">Adres</Label>
            <Textarea rows={2} value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm mt-1" /></div>
          <div><Label className="text-xs uppercase font-mono text-muted-foreground">Notlar</Label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Müşteri Puanı</Label>
              <select
                data-testid="edit-rating"
                value={form.rating || "B"}
                onChange={(e) => setForm({ ...form, rating: e.target.value })}
                className="bg-card border border-border rounded-sm mt-1 h-10 px-3 text-sm w-full"
              >
                <option value="A+">A+ · Premium</option>
                <option value="A">A · Yüksek</option>
                <option value="B">B · Standart</option>
                <option value="C">C · Riskli</option>
              </select>
            </div>
            <div className="flex flex-col">
              <Label className="text-xs uppercase font-mono text-muted-foreground">VIP</Label>
              <label className="mt-1 flex items-center gap-2 h-10 px-3 bg-card border border-border rounded-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="edit-vip"
                  checked={!!form.is_vip}
                  onChange={(e) => setForm({ ...form, is_vip: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm">VIP Müşteri</span>
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Müşteri Temsilcisi</Label>
              <Select value={repValue} onValueChange={onRepChange}>
                <SelectTrigger data-testid="edit-sales-rep-select" className="rounded-sm mt-1">
                  <SelectValue placeholder="Personel seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="edit-sales-rep-none">Atanmadı</SelectItem>
                  {repValue === "__custom" && (
                    <SelectItem value="__custom" data-testid="edit-sales-rep-custom">
                      {form.sales_rep_name} (mevcut kayıt)
                    </SelectItem>
                  )}
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id} data-testid={`edit-sales-rep-emp-${emp.id}`}>
                      {emp.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Temsilci E-posta</Label>
              <Input data-testid="edit-sales-rep-email" value={form.sales_rep_email || ""} readOnly disabled
                placeholder="Personel kaydından gelir" className="rounded-sm mt-1 opacity-60" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Kredi Limiti (₺)</Label>
              <Input type="number" step="0.01" data-testid="edit-credit-limit" value={form.credit_limit || 0}
                onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}
                className="rounded-sm mt-1 font-mono" />
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Risk Limiti (₺)</Label>
              <Input type="number" step="0.01" data-testid="edit-risk-limit" value={form.risk_limit || 0}
                onChange={(e) => setForm({ ...form, risk_limit: e.target.value })}
                className="rounded-sm mt-1 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
            <Button type="submit" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Visits Tab -------------------------------------------------------------
function VisitsTab({ customerId }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ summary: "", rep_name: "", next_visit_at: "", reminder_note: "" });
  const [gps, setGps] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/visits`);
      setVisits(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line

  const captureGps = () => {
    if (!("geolocation" in navigator)) return toast.error("GPS desteklenmiyor");
    toast.loading("Konum alınıyor…", { id: "geo" });
    navigator.geolocation.getCurrentPosition(
      (p) => { setGps({ lat: p.coords.latitude, lng: p.coords.longitude }); toast.success("Konum kaydedildi", { id: "geo" }); },
      (err) => toast.error(err.message, { id: "geo" }),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.summary.trim()) return toast.error("Görüşme notu zorunlu");
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/visits`, {
        customer_id: customerId,
        rep_name: form.rep_name || "",
        summary: form.summary,
        next_visit_at: form.next_visit_at ? new Date(form.next_visit_at).toISOString() : null,
        reminder_note: form.reminder_note || "",
        location_lat: gps?.lat ?? null,
        location_lng: gps?.lng ?? null,
        visited_at: new Date().toISOString(),
      });
      toast.success("Ziyaret kaydedildi");
      setAddOpen(false);
      setForm({ summary: "", rep_name: "", next_visit_at: "", reminder_note: "" });
      setGps(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (v) => {
    if (!window.confirm("Bu ziyaret silinsin mi?")) return;
    try {
      await api.delete(`/customers/${customerId}/visits/${v.id}`);
      setVisits((prev) => prev.filter((x) => x.id !== v.id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-widest">Ziyaret Kayıtları</h3>
        <Button data-testid="new-visit-btn" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> Yeni Ziyaret
        </Button>
      </div>

      {loading ? <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></div>
       : visits.length === 0 ? <PlaceholderCard title="Kayıt yok" note="Bu müşteriye ait ziyaret bulunmuyor." />
       : (
        <div className="space-y-3">
          {visits.map((v) => (
            <Card key={v.id} data-testid={`visit-${v.id}`} className="rounded-sm bg-card border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <User className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-sm font-bold">{v.rep_name || "—"}</span>
                    <span className="text-xs text-muted-foreground font-mono">· {fmtDate(v.visited_at)}</span>
                  </div>
                  <div className="text-sm mb-2">{v.summary}</div>
                  {v.next_visit_at && (
                    <div className="text-xs text-primary font-mono">→ Sonraki ziyaret: {fmtDate(v.next_visit_at)}</div>
                  )}
                  {v.reminder_note && (
                    <div className="text-xs text-muted-foreground italic mt-1">Hatırlatma: {v.reminder_note}</div>
                  )}
                  {v.location_lat && v.location_lng && (
                    <a href={`https://www.google.com/maps/@${v.location_lat},${v.location_lng},16z`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-500 mt-1">
                      <MapPin className="w-3 h-3" /> Konumu Aç
                    </a>
                  )}
                </div>
                <button onClick={() => remove(v)} className="text-muted-foreground hover:text-rose-500 p-1" title="Sil">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle>Yeni Ziyaret Kaydı</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Görüşme Notu *</Label>
              <Textarea data-testid="visit-summary" rows={3} required value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase font-mono text-muted-foreground">Satış Temsilcisi</Label>
                <Input value={form.rep_name} onChange={(e) => setForm({ ...form, rep_name: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase font-mono text-muted-foreground">Sonraki Ziyaret</Label>
                <Input type="date" value={form.next_visit_at} onChange={(e) => setForm({ ...form, next_visit_at: e.target.value })} className="rounded-sm mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Hatırlatma</Label>
              <Input value={form.reminder_note} onChange={(e) => setForm({ ...form, reminder_note: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-sm">
              <div className="text-xs">
                <div className="uppercase font-mono text-muted-foreground">Konum (GPS)</div>
                <div className="font-mono mt-1">{gps ? `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}` : "—"}</div>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={captureGps}><MapPin className="w-3.5 h-3.5 mr-1" /> {gps ? "Yenile" : "Konum Al"}</Button>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>İptal</Button>
              <Button data-testid="visit-save-btn" type="submit" disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null} Kaydet</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Notes Tab --------------------------------------------------------------
// ---- Documents Tab ---------------------------------------------------------
const DOC_EXT_COLORS = {
  pdf: "bg-rose-500/15 text-rose-500 border-rose-500/40",
  doc: "bg-sky-500/15 text-sky-500 border-sky-500/40",
  docx: "bg-sky-500/15 text-sky-500 border-sky-500/40",
  xls: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
  xlsx: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
  csv: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
  jpg: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  jpeg: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  png: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  webp: "bg-amber-500/15 text-amber-500 border-amber-500/40",
  svg: "bg-violet-500/15 text-violet-500 border-violet-500/40",
  ai: "bg-blue-500/15 text-blue-500 border-blue-500/40",
  cdr: "bg-blue-500/15 text-blue-500 border-blue-500/40",
  psd: "bg-indigo-500/15 text-indigo-500 border-indigo-500/40",
  zip: "bg-slate-500/15 text-slate-500 border-slate-500/40",
  rar: "bg-slate-500/15 text-slate-500 border-slate-500/40",
};
const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

function DocumentsTab({ customerId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [file, setFile] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/documents`);
      setDocs(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line

  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label || "");
      await api.post(`/customers/${customerId}/documents/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFile(null); setLabel(""); setOpenDialog(false);
      await load();
      toast.success("Evrak yüklendi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`"${d.original_filename}" silinsin mi?`)) return;
    try {
      await api.delete(`/customers/${customerId}/documents/${d.id}`);
      setDocs((prev) => prev.filter((x) => x.id !== d.id));
      toast.success("Silindi");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/customers/${customerId}/documents/${d.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest font-mono text-muted-foreground">Belgeler</div>
          <div className="text-sm text-muted-foreground mt-0.5">
            {docs.length} evrak · PDF, DOCX, XLSX, JPG/PNG/SVG, AI/CDR/PSD, ZIP (max 25MB)
          </div>
        </div>
        <Button
          data-testid="add-document-btn"
          size="sm"
          onClick={() => setOpenDialog(true)}
          className="rounded-sm"
        >
          <Upload className="w-4 h-4 mr-1.5" /> Evrak Ekle
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Yükleniyor...
        </div>
      ) : docs.length === 0 ? (
        <Card className="rounded-sm bg-card border-border border-dashed p-10 text-center">
          <FileIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
          <div className="font-bold mb-1">Henüz evrak yok</div>
          <div className="text-sm text-muted-foreground mb-4">
            Sözleşme, fiyat listesi, marka kılavuzu vb. dosyaları buradan yükleyin.
          </div>
          <Button data-testid="add-document-empty-btn" size="sm" onClick={() => setOpenDialog(true)} className="rounded-sm">
            <Upload className="w-4 h-4 mr-1.5" /> İlk Evrakı Ekle
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {docs.map((d) => {
            const ext = (d.original_filename.split(".").pop() || "").toLowerCase();
            const color = DOC_EXT_COLORS[ext] || "bg-muted text-muted-foreground border-border";
            return (
              <Card
                key={d.id}
                data-testid={`doc-${d.id}`}
                className="rounded-sm bg-card border-border p-4 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center text-[10px] font-mono font-bold uppercase border ${color} shrink-0`}>
                    {ext.slice(0, 4)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate" title={d.original_filename}>
                      {d.original_filename}
                    </div>
                    {d.label && (
                      <div className="text-xs text-muted-foreground truncate mt-0.5">{d.label}</div>
                    )}
                    <div className="text-[11px] text-muted-foreground font-mono mt-1">
                      {fmtBytes(d.size)} · {fmtDate(d.created_at)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border">
                  <Button
                    data-testid={`doc-download-${d.id}`}
                    variant="outline" size="sm"
                    onClick={() => download(d)}
                    className="rounded-sm flex-1"
                  >
                    <Download className="w-3.5 h-3.5 mr-1" /> İndir
                  </Button>
                  <Button
                    data-testid={`doc-delete-${d.id}`}
                    variant="ghost" size="sm"
                    onClick={() => remove(d)}
                    className="rounded-sm text-rose-500 hover:text-rose-600 hover:bg-rose-500/10"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle>Evrak Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Etiket (opsiyonel)</Label>
              <Input
                data-testid="doc-label-input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Örn: 2026 sözleşmesi"
                className="rounded-sm mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-mono text-muted-foreground">Dosya *</Label>
              <Input
                data-testid="doc-file-input"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="rounded-sm mt-1.5"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png,.webp,.gif,.svg,.ai,.cdr,.psd,.eps,.zip,.rar,.7z,.txt"
              />
              {file && (
                <div className="text-xs text-muted-foreground mt-2 font-mono">
                  {file.name} · {fmtBytes(file.size)}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenDialog(false)} className="rounded-sm">İptal</Button>
            <Button
              data-testid="doc-upload-btn"
              onClick={upload}
              disabled={!file || uploading}
              className="rounded-sm"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
              Yükle
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function NotesTab({ customerId }) {
  const [notes, setNotes] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/customers/${customerId}/notes`);
      setNotes(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [customerId]); // eslint-disable-line

  const add = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post(`/customers/${customerId}/notes`, { customer_id: customerId, text });
      setText("");
      await load();
      toast.success("Not eklendi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (n) => {
    if (!window.confirm("Not silinsin mi?")) return;
    try {
      await api.delete(`/customers/${customerId}/notes/${n.id}`);
      setNotes((prev) => prev.filter((x) => x.id !== n.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <Card className="rounded-sm bg-card border-border p-4">
        <Label className="text-xs uppercase font-mono text-muted-foreground">Yeni Not</Label>
        <Textarea data-testid="note-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} className="rounded-sm mt-2 mb-3" placeholder="Örn: Telefonda görüştük, önümüzdeki ay 200 adet iş tulumu sipariş edecekler..." />
        <div className="flex justify-end">
          <Button data-testid="note-save-btn" size="sm" onClick={add} disabled={saving || !text.trim()}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Not Ekle
          </Button>
        </div>
      </Card>

      {notes.length === 0 ? (
        <PlaceholderCard title="Not yok" note="İlk notu yukarıdan ekleyin." />
      ) : (
        <div className="space-y-3">
          {notes.map((n) => (
            <Card key={n.id} data-testid={`note-${n.id}`} className="rounded-sm bg-card border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-muted-foreground font-mono mb-2">
                    {n.author || "—"} · {fmtDate(n.created_at)}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{n.text}</div>
                </div>
                <button onClick={() => remove(n)} className="text-muted-foreground hover:text-rose-500 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Timeline Panel --------------------------------------------------------
function TimelinePanel({ customerId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/customers/${customerId}/timeline`);
        setEvents(data.events || []);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
  }, [customerId]);

  const iconOf = (t) => ({
    order: ShoppingCart, quote: FileText, shipment: Truck, visit: Users,
    note: MessageSquare,
  })[t] || Info;
  const colorOf = (t) => ({
    order: "text-blue-500", quote: "text-purple-500", shipment: "text-emerald-500",
    visit: "text-amber-500", note: "text-cyan-500",
  })[t] || "text-slate-500";

  if (loading) return <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline text-primary" /></div>;
  if (events.length === 0) return null;

  return (
    <Card className="rounded-sm bg-card border-border p-5" data-testid="timeline-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest mb-4">Aktivite Zaman Çizelgesi</h3>
      <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
        {events.slice(0, 30).map((e, i) => {
          const Icon = iconOf(e.type);
          return (
            <div key={i} data-testid={`timeline-${e.type}`} className="flex gap-3 items-start pb-3 border-b border-border last:border-0">
              <div className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 ${colorOf(e.type)}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <div className="text-sm font-semibold truncate">{e.title}</div>
                  <div className="text-[10px] font-mono text-muted-foreground whitespace-nowrap">{fmtDate(e.at)}</div>
                </div>
                <div className="text-xs text-muted-foreground truncate">{e.detail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// --- Customer Tasks section (FAZ 4 — görev ↔ müşteri linki) ---------------
function CustomerTasks({ customerId }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/tasks?customer_id=${customerId}&scope=all`)
      .then((r) => setTasks(r.data || []))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  }, [customerId]);

  const active = tasks.filter((t) => !["tamamlandi", "iptal"].includes(t.status));
  const completed = tasks.filter((t) => t.status === "tamamlandi");

  return (
    <Card className="rounded-sm bg-card border-border p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold uppercase tracking-widest flex items-center gap-2">
          Bu Müşteriye İlişkin Görevler
          <span className="text-[11px] font-mono text-muted-foreground normal-case">
            ({active.length} açık · {completed.length} tamamlandı)
          </span>
        </h3>
        <button
          onClick={() => navigate(`/gorevler?customer_id=${customerId}`)}
          className="text-xs text-primary hover:underline"
        >
          Tüm Görevler →
        </button>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground py-4">Yükleniyor…</div>
      ) : tasks.length === 0 ? (
        <div className="text-center py-8 text-xs text-muted-foreground border border-dashed border-border rounded-sm">
          Bu müşteriye bağlı görev yok.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {tasks.slice(0, 10).map((t) => {
            const isOver = t.due_date && !["tamamlandi", "iptal"].includes(t.status) && new Date(t.due_date) < new Date();
            return (
              <div key={t.id}
                data-testid={`customer-task-${t.id}`}
                onClick={() => navigate(`/gorevler`)}
                className={`flex items-center gap-2 py-1.5 px-2 rounded-sm cursor-pointer hover:bg-muted/30 border ${
                  isOver ? "border-red-900/40 bg-red-950/10" : "border-transparent"
                }`}
              >
                <span className={`text-[10px] px-1.5 py-0.5 border rounded-sm uppercase font-mono ${
                  t.priority === "kritik" ? "border-red-900/60 text-red-300 bg-red-950/30" :
                  t.priority === "yuksek" ? "border-blue-900/60 text-blue-300 bg-blue-950/30" :
                  "border-slate-800 text-slate-400 bg-slate-900/40"
                }`}>{t.priority}</span>
                <span className="text-sm text-foreground flex-1 truncate">{t.title}</span>
                <span className="text-[11px] font-mono text-muted-foreground">
                  {t.status}
                </span>
                {t.main_assignee_email && (
                  <span className="text-[11px] font-mono text-muted-foreground">
                    · {t.main_assignee_email.split("@")[0]}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---- FAZ SO1 — Müşteri aktif stok opsiyonları ------------------------------
function CustomerStockOptions({ customerId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/inventory/stock-options", { params: { customer_id: customerId } });
        if (alive) setRows((data || []).filter((o) => o.status === "active"));
      } catch { /* sessiz */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [customerId]);

  if (loading) return <div className="text-xs text-muted-foreground py-8 text-center">Yükleniyor…</div>;
  return (
    <div className="rounded-sm border border-border bg-card" data-testid="customer-options-card">
      <div className="px-4 py-2.5 border-b border-border text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
        Aktif Stok Opsiyonları
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-xs text-muted-foreground">Bu müşteri için aktif stok opsiyonu yok.</div>
      ) : (
        <div className="divide-y divide-border/60">
          {rows.map((o) => (
            <div key={o.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-xs" data-testid={`customer-option-row-${o.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-foreground font-medium truncate">{o.product_name}</div>
                <div className="text-[10px] font-mono text-muted-foreground truncate">
                  {o.variant_sku || "Genel"} · {o.warehouse_name} · Bitiş: {o.expires_at}
                  {o.note ? ` · ${o.note}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-foreground tabular-nums font-semibold">{Number(o.quantity).toLocaleString("tr-TR", { maximumFractionDigits: 3 })}</span>
                {o.is_expired ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/15 text-amber-400">Süresi Doldu</span>
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/15 text-emerald-400">Aktif</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
